/**
 * Inline VAST 3.0 builder for the CSAI validation tool.
 *
 * The four test creatives live in `frontend-v3/public/ads/ad-{1..4}.mp4`
 * (1280x720, H.264 + AAC, ~10 s each). We build the VAST document in-memory and
 * hand it to IMA via `adsRequest.adsResponse` (NOT `adTagUrl`) — no ad server,
 * no hosted XML file, no CORS: the MediaFile URLs are same-origin
 * (`${origin}/ads/ad-N.mp4`) so the browser fetches them straight from Vite.
 */

export const AD_CREATIVES = [
  { id: 'ad-1', title: 'AD 1', durationSec: 10 },
  { id: 'ad-2', title: 'AD 2', durationSec: 10 },
  { id: 'ad-3', title: 'AD 3', durationSec: 10 },
  { id: 'ad-4', title: 'AD 4', durationSec: 10 },
] as const;

export type VastMode = 'single' | 'pod';

function hms(totalSec: number): string {
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = Math.floor(totalSec % 60);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(h)}:${p(m)}:${p(s)}`;
}

function inlineAd(
  creative: (typeof AD_CREATIVES)[number],
  origin: string,
  sequence?: number,
): string {
  const seqAttr = sequence ? ` sequence="${sequence}"` : '';
  const url = `${origin}/ads/${creative.id}.mp4`;
  return `
  <Ad id="${creative.id}"${seqAttr}>
    <InLine>
      <AdSystem>CircaCSAITest</AdSystem>
      <AdTitle>${creative.title}</AdTitle>
      <Impression><![CDATA[about:blank]]></Impression>
      <Creatives>
        <Creative>
          <Linear>
            <Duration>${hms(creative.durationSec)}</Duration>
            <MediaFiles>
              <MediaFile delivery="progressive" type="video/mp4" width="1280" height="720" scalable="true" maintainAspectRatio="true">
                <![CDATA[${url}]]>
              </MediaFile>
            </MediaFiles>
          </Linear>
        </Creative>
      </Creatives>
    </InLine>
  </Ad>`;
}

/**
 * Build a VAST 3.0 document string.
 *  - `single` → one ad (rotates through the four creatives by `rotation`).
 *  - `pod`    → all four creatives as a sequenced ad pod (tests pod rotation +
 *               CONTENT_RESUME firing only after the LAST ad completes).
 */
export function buildVast(mode: VastMode, origin: string, rotation = 0): string {
  const ads =
    mode === 'pod'
      ? AD_CREATIVES.map((c, i) => inlineAd(c, origin, i + 1)).join('\n')
      : inlineAd(AD_CREATIVES[rotation % AD_CREATIVES.length], origin);
  return `<?xml version="1.0" encoding="UTF-8"?>
<VAST version="3.0">${ads}
</VAST>`;
}
